import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import { ReactComponent as ArrowUpIcon } from '@doveconviene/sf-ui/icons/arrow_up.svg'
import { ReactComponent as ArrowDownIcon } from '@doveconviene/sf-ui/icons/arrow_down.svg'
import useOnWindowResize from '../../../../ui/hooks/useOnWindowResize'

import Icon from '../../../../ui/Icon'

const StyledRoot = styled.div(({ theme: { spaces } }) => ({
  padding: `${spaces.default}px ${spaces.default / 2}px ${spaces.default / 2}px`,
  maxWidth: '500px',
  margin: '0 auto',
}))

const StyledDescription = styled.div(({ theme: { colors, spaces } }) => ({
  border: `1px solid ${colors.white}`,
  borderWidth: '1px 0',
  paddingBottom: spaces.default,
}))

const limitRowsNumber = lineClamp => {
  if (!lineClamp) {
    return {}
  }

  return {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: lineClamp,
    lineClamp,
    WebkitBoxOrient: 'vertical',
  }
}

const calcStickyHeader = (top, { spaces }) => {
  if (!top) return {}

  return {
    maxWidth: `calc(500px - ${spaces.default}px)`,
    margin: '0 auto',
    zIndex: '2',
    top,
    position: 'fixed',
    willChange: 'top',
    right: (spaces.default / 2) - 1,
    left: (spaces.default / 2) - 1,
    paddingRight: 1,
    paddingLeft: 1,
  }
}

const HEADER_HEIGHT = 55
const StyledHeader = styled.div(({ theme: { spaces, colors }, disabled, sticky }) => ({
  cursor: disabled ? 'default' : 'pointer',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${spaces.default}px 0`,
  backgroundColor: colors.light_grey,
  height: HEADER_HEIGHT - 1,
  ...calcStickyHeader(sticky, { spaces }),
}))

const StyledContent = styled.div(({ sticky, showDescription, theme: { fontSize } }) => ({
  position: 'relative',
  overflow: 'hidden',
  fontSize: fontSize * 0.875,
  lineHeight: '20px',
  ...limitRowsNumber(showDescription ? 0 : '2'),
  paddingTop: sticky ? HEADER_HEIGHT : 0,
}))
const StyledTitle = styled.div(({ theme: { fontSize } }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  fontWeight: 600,
  fontSize: fontSize * 1.25,
}))
const StyledArrow = styled.div(({ theme: { spaces, colors } }) => ({
  display: 'inline-block',
  width: spaces.large,
  height: spaces.large,
  margin: `-${spaces.default / 8}px 0 0 0`,
  color: colors.primary,
}))

const ProductDescription = ({
  description, onSeeMoreClick, descriptionLabel, productId, containerRef,
}) => {
  const [isDescriptionExpanded, setDescriptionExpanded] = useState(false)
  const [isShowMoreVisible, setShowMoreVisible] = useState(false)
  const [sticky, setStickyPosition] = useState(0)
  const descriptionRef = useRef()
  const bottomContentRef = React.useRef(null)

  const getDescriptionCoordinates = () => descriptionRef?.current || {}

  const onMoreClick = () => {
    setDescriptionExpanded(!isDescriptionExpanded)
    onSeeMoreClick(!isDescriptionExpanded)
    setStickyPosition(0)
  }

  const updateSeeMore = () => {
    const {
      scrollHeight,
      offsetHeight,
    } = getDescriptionCoordinates()

    const expanded = scrollHeight > offsetHeight
    setShowMoreVisible(expanded)
  }

  useOnWindowResize(updateSeeMore)
  React.useEffect(() => {
    updateSeeMore()
    return () => {
      setDescriptionExpanded(false)
      setShowMoreVisible(false)
    }
  }, [isShowMoreVisible, description, productId])

  const onScroll = () => {
    const scrollTopPosition = containerRef?.scrollTop
    const {
      offsetTop,
      offsetHeight,
    } = bottomContentRef?.current || {}

    const BottomOffsetLimit = offsetTop + offsetHeight
    const isSticky = (
      scrollTopPosition >= offsetTop
      && isDescriptionExpanded
      && scrollTopPosition <= BottomOffsetLimit
    )

    if (isSticky) {
      setStickyPosition(Math.min(scrollTopPosition - 1, BottomOffsetLimit - HEADER_HEIGHT))
    } else setStickyPosition(0)
  }

  React.useLayoutEffect(() => {
    if (containerRef && isShowMoreVisible) {
      containerRef.addEventListener('scroll', onScroll)
      onScroll()
      return () => containerRef.removeEventListener('scroll', onScroll)
    }

    setStickyPosition(0)
    return () => false
  }, [isDescriptionExpanded])

  const htmlFormatDescription = description.replace(/\n/g, '<br/>')

  return (
    <StyledRoot>
      <StyledDescription ref={bottomContentRef}>
        <StyledHeader
          onClick={isShowMoreVisible ? onMoreClick : null}
          disabled={!isShowMoreVisible}
          sticky={sticky}
          data-test="ProductDescriptionHeader"
        >
          <StyledTitle>{descriptionLabel}</StyledTitle>
          {
            isShowMoreVisible && (
              <StyledArrow>
                <Icon>
                  {isDescriptionExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
                </Icon>
              </StyledArrow>
            )
          }
        </StyledHeader>
        <StyledContent
          sticky={sticky}
          data-test="ProductDescription"
          ref={descriptionRef}
          showDescription={isDescriptionExpanded}
          dangerouslySetInnerHTML={{ __html: htmlFormatDescription }}
        />
      </StyledDescription>
    </StyledRoot>
  )
}

ProductDescription.propTypes = {
  description: PropTypes.string,
  onSeeMoreClick: PropTypes.func,
  descriptionLabel: PropTypes.string,
  productId: PropTypes.string,
  containerRef: PropTypes.shape({
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
    scrollTop: PropTypes.number,
  }),
}

ProductDescription.defaultProps = {
  description: '',
  descriptionLabel: '',
  onSeeMoreClick: () => false,
  productId: '',
  containerRef: null,
}

ProductDescription.displayName = 'ProductDescription'
ProductDescription.whyDidYouRender = true

export default React.memo(ProductDescription)
